import { Point } from '@ardoq/math';
import { ChartWindowState } from '@ardoq/graph';
import { dispatchAction } from '@ardoq/rxbeach';
import { setScale, setSelectedQuadrant } from './actions';
import { Quadrant } from './types';

const ANIMATION_DURATION = 300;

const interpolate = (
  start: number,
  end: number,
  transformFactor: number
): number => start + (end - start) * transformFactor;

const interpolatePoint = (
  start: Point,
  end: Point,
  transformFactor: number
): Point => [
  interpolate(start[0], end[0], transformFactor),
  interpolate(start[1], end[1], transformFactor),
];

const interpolateWindowState = (
  startState: ChartWindowState,
  endState: ChartWindowState,
  transformFactor: number
): ChartWindowState => ({
  windowCenter: interpolatePoint(
    startState.windowCenter,
    endState.windowCenter,
    transformFactor
  ),
  windowScale: interpolate(
    startState.windowScale,
    endState.windowScale,
    transformFactor
  ),
});

const animateWindowState = (
  startState: ChartWindowState,
  endState: ChartWindowState,
  durationMs: number,
  onFrame: (state: ChartWindowState) => void,
  onComplete: () => void
): void => {
  const startTime = performance.now();

  const animate = (currentTime: number): void => {
    const elapsedTime = currentTime - startTime;
    const progress = Math.min(elapsedTime / durationMs, 1);

    const easedProgress = 1 - Math.pow(1 - progress, 2);

    const currentState = interpolateWindowState(
      startState,
      endState,
      easedProgress
    );

    onFrame(currentState);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else {
      onComplete();
    }
  };

  requestAnimationFrame(animate);
};

type ZoomToQuadrantArgs = {
  currentWindowState: ChartWindowState;
  targetWindowState: ChartWindowState | null;
  quadrant: Quadrant | null;
  viewInstanceId: string;
};

export const zoomToQuadrant = ({
  currentWindowState,
  targetWindowState,
  quadrant,
  viewInstanceId,
}: ZoomToQuadrantArgs): void => {
  if (!targetWindowState) {
    return;
  }

  dispatchAction(
    setSelectedQuadrant({
      selectedQuadrant: null,
    })
  );
  animateWindowState(
    currentWindowState,
    targetWindowState,
    ANIMATION_DURATION,
    state => {
      dispatchAction(setScale(state), viewInstanceId);
    },
    () => {
      dispatchAction(setScale(targetWindowState), viewInstanceId);
      dispatchAction(
        setSelectedQuadrant({
          selectedQuadrant: quadrant,
        })
      );
    }
  );
};
